import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { setAccessToken } from "../lib/token-store";
import { PendingInvitationsBanner } from "../components/PendingInvitationsBanner";

interface MeResponse {
  user: { uuid: string; email: string; isPlatformAdmin: boolean };
  organisations: Array<{
    uuid: string;
    name: string;
    slug: string;
    tier: string;
    role: "Admin" | "Contributor" | "Auditor_Viewer";
    activeModules: string[];
  }>;
}

const ROLE_LABEL: Record<MeResponse["organisations"][number]["role"], string> = {
  Admin: "Admin",
  Contributor: "Contributor",
  Auditor_Viewer: "Auditor (read-only)",
};

export function SwitchOrg() {
  const { status, refreshSession } = useAuth();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [target, setTarget] = useState<{ slug: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await api<MeResponse>("/auth/me");
      setMe(res);
    } catch (err) {
      setErrors(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (status === "anonymous") return <Navigate to="/login" replace />;
  if (target) return <Navigate to={`/o/${target.slug}/dashboard`} replace />;

  if (!me) return <p className="p-6 text-sm text-slate-500 dark:text-slate-400">Loading…</p>;

  const switchTo = async (uuid: string, slug: string) => {
    setBusy(true);
    setErrors([]);
    try {
      const res = await api<{ accessToken: string }>("/auth/switch-org", {
        method: "POST",
        body: { organisationUuid: uuid },
      });
      setAccessToken(res.accessToken);
      await refreshSession();
      setTarget({ slug });
    } catch (err) {
      setErrors(formatApiError(err));
      setBusy(false);
    }
  };

  const orgsByRole = {
    auditor: me.organisations.filter((o) => o.role === "Auditor_Viewer"),
    member: me.organisations.filter((o) => o.role !== "Auditor_Viewer"),
  };

  return (
    <main id="main" className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <div className="mx-auto max-w-3xl space-y-6 p-6">
        <header>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
            Switch organisation
          </h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Pick an organisation to enter. Your role determines what you can see and do inside.
          </p>
        </header>

        <PendingInvitationsBanner />

        {errors.length > 0 ? (
          <div
            role="alert"
            className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
          >
            {errors[0]}
          </div>
        ) : null}

        {me.organisations.length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
            You are not yet a member of any organisation. Wait for an invitation to appear, or sign
            up to create your own organisation.
          </p>
        ) : null}

        {orgsByRole.auditor.length > 0 ? (
          <section>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              As an auditor (read-only)
            </h2>
            <ul className="mt-2 space-y-2">
              {orgsByRole.auditor.map((org) => (
                <OrgRow
                  key={org.uuid}
                  org={org}
                  busy={busy}
                  onClick={() => void switchTo(org.uuid, org.slug)}
                />
              ))}
            </ul>
          </section>
        ) : null}

        {orgsByRole.member.length > 0 ? (
          <section>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {orgsByRole.auditor.length > 0 ? "As a member" : "Your organisations"}
            </h2>
            <ul className="mt-2 space-y-2">
              {orgsByRole.member.map((org) => (
                <OrgRow
                  key={org.uuid}
                  org={org}
                  busy={busy}
                  onClick={() => void switchTo(org.uuid, org.slug)}
                />
              ))}
            </ul>
          </section>
        ) : null}
      </div>
    </main>
  );
}

function OrgRow({
  org,
  busy,
  onClick,
}: {
  org: MeResponse["organisations"][number];
  busy: boolean;
  onClick: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        disabled={busy}
        onClick={onClick}
        className="flex w-full items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-4 text-left hover:border-brand-500 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800"
      >
        <div>
          <p className="font-medium text-slate-900 dark:text-slate-100">{org.name}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {org.tier.replace("_", " ")} · {ROLE_LABEL[org.role]} · /{org.slug}
          </p>
        </div>
        <span className="rounded-md bg-brand-600 px-3 py-1 text-xs font-semibold text-white">
          Enter →
        </span>
      </button>
    </li>
  );
}
